// =====================================================================
// paymentSchedule — the single source of truth for billing models.
//
// One canonical set of billing-model keys is used everywhere: Settings
// (which plans a clinic offers + the default), the checkout billing-model
// picker, the collection math (get_plan_billing_state), and the proposal
// PDF. The keys match the checkout `payment_preset` vocabulary so the
// money engine never needs a translation layer.
//
// Canonical keys (must match the DB CHECK on
// clinic_settings.payment_schedule_type AND treatment_plans.payment_preset):
//
//   per_phase  — total / N phases per phase. Most predictable for the
//                patient. The recommended default.
//
//   50_50      — 50% on first phase, 50% on last, middle phases ₱0.
//                Deposit at commencement, balance at completion.
//
//   full       — "Pay in Full": entire plan total on the FIRST phase,
//                ₱0 elsewhere. Everything collected upfront.
//
//   custom     — "Flexible": at least a minimum down payment, the balance
//                arranged at checkout. No fixed per-phase distribution.
//
// Rounding:
//   Centavos are integer. We split with Math.floor and bank the
//   remainder on the LAST non-zero phase so per-phase amounts sum to the
//   input total exactly. Patients never see a "₱0.33 missing."
// =====================================================================

export const PAYMENT_SCHEDULE_TYPES = ["per_phase", "50_50", "full", "custom"];

export const PAYMENT_SCHEDULE_DEFAULT = "per_phase";

export const PAYMENT_SCHEDULE_LABELS = {
  per_phase: "Even split",
  "50_50":   "Deposit + balance (50/50)",
  full:      "Pay in Full",
  custom:    "Flexible",
};

export const PAYMENT_SCHEDULE_DESCRIPTIONS = {
  per_phase: "Plan total divided evenly across appointments.",
  "50_50":   "50% on the first appointment, 50% on the last appointment.",
  full:      "Entire plan total due on the first appointment.",
  custom:    "At least a minimum down payment, balance arranged at checkout.",
};

// Legacy keys (pre-SSOT) → canonical. Kept so any stale value that
// slips through during the transition still resolves to the right model
// instead of falling back to the default.
const LEGACY_KEY_ALIASES = {
  even_split:      "per_phase",
  deposit_balance: "50_50",
  plan_level:      "full",
  flexible:        "custom",
};

// Format a flexible plan's per-PDF summary line. The minimum is stored
// as a percentage in clinic_settings; this helper computes the absolute
// amount for the patient's specific plan total. Null/zero pct means
// "any down payment" — patient and clinic agree at checkout.
export function formatFlexibleSummary(pct, planTotalCentavos) {
  const pctNum = Number(pct);
  if (!Number.isFinite(pctNum) || pctNum <= 0) {
    return "Any down payment, balance arranged at checkout.";
  }
  const total = Math.max(0, Math.round(Number(planTotalCentavos) || 0));
  const minCentavos = Math.round((total * pctNum) / 100);
  const phpStr = "Php " + Math.round(minCentavos / 100).toLocaleString("en-PH");
  // Trim trailing ".00" / ".0" so "20%" reads as "20%" rather than "20.00%".
  const pctLabel = (Math.round(pctNum * 100) / 100).toString().replace(/\.0+$/, "");
  return `At least ${pctLabel}% down (${phpStr}), balance arranged at checkout.`;
}

/**
 * Normalize a schedule string into a canonical type. Legacy keys are
 * mapped forward; unknown / null values fall back to the default so a
 * PDF never blows up because a settings row is malformed mid-deploy.
 *
 * @param {string|null|undefined} raw
 * @returns {"per_phase"|"50_50"|"full"|"custom"}
 */
export function normalizeScheduleType(raw) {
  if (PAYMENT_SCHEDULE_TYPES.includes(raw)) return raw;
  if (raw != null && LEGACY_KEY_ALIASES[raw]) return LEGACY_KEY_ALIASES[raw];
  return PAYMENT_SCHEDULE_DEFAULT;
}

/**
 * Compute per-phase payment amounts (centavos) for a given total + phase
 * count + schedule type. The returned array has length === phaseCount,
 * indexed 0..N-1 (so phase 1 is at index 0). Sum across the array
 * equals `totalCentavos` exactly — rounding remainder lands on the
 * last non-zero phase.
 *
 * Edge cases:
 *   - phaseCount <= 0      → returns [] (caller decides how to render)
 *   - phaseCount === 1     → returns [totalCentavos] regardless of type
 *   - totalCentavos <= 0   → returns array of zeros
 *
 * @param {number} totalCentavos
 * @param {number} phaseCount
 * @param {string} scheduleType
 * @returns {number[]} per-phase centavos amounts
 */
export function computeScheduleAmounts(totalCentavos, phaseCount, scheduleType) {
  const total = Math.max(0, Math.round(Number(totalCentavos) || 0));
  const N = Math.max(0, Math.floor(Number(phaseCount) || 0));
  const type = normalizeScheduleType(scheduleType);

  if (N <= 0) return [];
  if (total <= 0) return new Array(N).fill(0);
  if (N === 1)  return [total];

  if (type === "full") {
    // Pay in Full — entire total on the FIRST phase, rest zero.
    const out = new Array(N).fill(0);
    out[0] = total;
    return out;
  }

  if (type === "custom") {
    // Flexible has no fixed per-phase distribution — patient negotiates
    // at checkout. Return zeros so any consumer that asks for per-phase
    // amounts gets a clear "no schedule" signal rather than leaking
    // the plan total into a phase that wasn't actually allocated.
    return new Array(N).fill(0);
  }

  if (type === "50_50") {
    const out = new Array(N).fill(0);
    const half = Math.floor(total / 2);
    out[0]     = half;
    out[N - 1] = total - half;
    return out;
  }

  // per_phase (default)
  const base = Math.floor(total / N);
  const out  = new Array(N).fill(base);
  // Bank the remainder on the last phase so the sum reconciles exactly.
  out[N - 1] = total - base * (N - 1);
  return out;
}
